import { FormResultForEmployeeElement } from "@/components/form-result/result-modal/components/FormResultForEmployeeElement.tsx";
import { PrintDocument, PrintHeader } from "@/components/print/index.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useEmployeeScoreCardResultsForFeedback } from "@/queries/recruit/use-scorecards.query.ts";
import { useEmployeeDetails } from "@/queries/employees/use-employees.query.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { Box, FullScreenPage, Stack, Text, Title } from "@skailer-com/uikit";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export function FeedbackPrintPage() {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { id, employee_id } = useParams();

  const feedbackId = id ? parseInt(id) : undefined;
  const employeeId = employee_id ? parseInt(employee_id) : undefined;

  const { data: results } = useEmployeeScoreCardResultsForFeedback({
    feedbackId: feedbackId!,
    employeeId: employeeId!,
    enabled: !!feedbackId && !!employeeId,
  });
  const { data: employee } = useEmployeeDetails(employeeId);

  const position = employee ? getEmployeePositionTitle(employee) : undefined;

  if (!results) {
    return <FullScreenPage />;
  }

  return (
    <FullScreenPage contentSize="lg" isCenterVertical={true}>
      <Box className="py-6 print:py-0">
        <PrintDocument
          header={
            <PrintHeader title={t("common:view_responses")}>
              {employee && (
                <Text className="text-base text-secondary">{renderEmployeeName(employee)}</Text>
              )}
              {position && <Text className="text-base text-secondary">{position}</Text>}
            </PrintHeader>
          }
          footerLabel={t("core:print_generated_with")}
        >
          <Box className="mt-9">
            <Stack>
              {results.pages.map((page, pageIndex) => (
                <Fragment key={`${pageIndex}`}>
                  <Stack direction={"horizontal"} gap={"md"}>
                    <Stack items={"end"} className={"w-16 min-h-7 pb-0.5"} justify={"end"}>
                      <Title size={"xs"} header={`${pageIndex + 1}`} />
                    </Stack>
                    <Stack className={"flex-1"} justify={"end"}>
                      <Title
                        header={page.title || t("common:page")}
                        size={"lg"}
                        paddingTop={true}
                      />
                    </Stack>
                  </Stack>
                  {page.sections.map((section, sectionIndex) => (
                    <Fragment key={`${sectionIndex}`}>
                      <Stack direction={"horizontal"} gap={"md"}>
                        <Stack items={"end"} className={"w-16 min-h-7 pb-0.5"} justify={"end"}>
                          <Title size={"xs"} header={`${pageIndex + 1}.${sectionIndex + 1}`} />
                        </Stack>
                        <Stack className={"flex-1"} justify={"end"}>
                          <Title
                            header={section.title || t("common:section")}
                            size={"md"}
                            paddingTop={true}
                          />
                        </Stack>
                      </Stack>
                      {section.elements.map((element, elementIndex) => (
                        <Fragment key={`${elementIndex}`}>
                          <Stack direction={"horizontal"} gap={"md"}>
                            <Stack
                              items={"end"}
                              className={"w-16 min-h-7 pt-6 shrink-0"}
                              justify={"start"}
                            >
                              <Title
                                size={"xs"}
                                header={`${pageIndex + 1}.${sectionIndex + 1}.${elementIndex + 1}`}
                              />
                            </Stack>
                            <Stack
                              className={
                                "flex-1 wrap-break-word overflow-hidden print:break-inside-avoid"
                              }
                              justify={"end"}
                            >
                              <FormResultForEmployeeElement
                                element={element}
                                answers={results.answers}
                              />
                            </Stack>
                          </Stack>
                        </Fragment>
                      ))}
                    </Fragment>
                  ))}
                </Fragment>
              ))}
            </Stack>
          </Box>
        </PrintDocument>
      </Box>
    </FullScreenPage>
  );
}
